/**
 * InlineMarkdown — lightweight markdown renderer for AI-generated text.
 *
 * Daniel reported that AI-generated content (Ask Winter chat, Decision
 * Room sections, etc.) was showing literal asterisks instead of bold.
 * The AI models reliably emit markdown — bold via **text**, italic via
 * *text*, inline code via `text`, etc. — and our render path was just
 * dumping the string into a JSX text node, so the markdown leaked.
 *
 * Rather than pull in a heavy library (react-markdown + remark plugins),
 * this is a focused renderer that handles the four patterns AI output
 * actually uses:
 *
 *   **bold**    → <strong>bold</strong>
 *   *italic*    → <em>italic</em>
 *   `code`      → <code>code</code>
 *   [text](url) → <a href="url" target="_blank">text</a>
 *   newlines    → preserved via white-space: pre-wrap on the wrapper
 *
 * Bullets and numbered lists are NOT processed at the block level —
 * we keep the original line breaks and let the wrapper's pre-wrap
 * styling do the layout. AI models almost always format bullets with
 * `- ` or `1. ` prefixes that render readably as-is in a pre-wrap
 * block; over-engineering list semantics would change the visual
 * rhythm without a clear benefit.
 *
 * Safe by construction: input is escaped before token replacement, and
 * the output is rendered via dangerouslySetInnerHTML on a parent that's
 * scoped to just the AI message body. No raw HTML passes through.
 */

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Render the four markdown patterns. Order matters:
 *   1) code spans first (they protect their contents from further
 *      mangling by emphasis matchers)
 *   2) links second (the URL inside [text](url) needs to be left alone
 *      by bold/italic)
 *   3) bold (** **) before italic (* *) — otherwise the bold's
 *      asterisks would each get matched as italic openers
 *   4) italic last
 */
function renderInlineMarkdown(raw) {
  let s = escapeHtml(raw);

  // Code spans: `something` → <code>something</code>
  s = s.replace(/`([^`\n]+)`/g, '<code style="background:rgba(148,163,184,0.18); padding:1px 5px; border-radius:4px; font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace; font-size:0.92em;">$1</code>');

  // Links: [text](http://url) → anchor. Restrict to http/https schemes
  // so we never render a `javascript:` URL even though escapeHtml
  // would already neuter the angle brackets.
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_, text, url) => {
    const safeUrl = url.replace(/"/g, '%22');
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="color:inherit; text-decoration:underline; text-underline-offset:2px;">${text}</a>`;
  });

  // Bold: **text** → <strong>text</strong>. Non-greedy, must span the
  // same line (no \n inside). The (?!\*) lookahead prevents collisions
  // with the leftover bare asterisks from italic patterns.
  s = s.replace(/\*\*([^\n*]+?)\*\*/g, '<strong>$1</strong>');

  // Italic: *text* → <em>text</em>. Same non-greedy + same-line
  // constraint. Run last so leftover single asterisks aren't already
  // wrapped inside a <strong>.
  s = s.replace(/(^|[^*])\*([^\n*]+?)\*(?!\*)/g, '$1<em>$2</em>');

  return s;
}

/**
 * Render an AI-generated string with inline markdown applied.
 *
 * Props:
 *   text     — the raw AI output to render
 *   tag      — wrapper element (default 'div'); use 'span' inside flow
 *              text or in places where the wrapper would otherwise break
 *              the surrounding line height
 *   style    — extra inline styles merged onto the wrapper. The
 *              wrapper always sets whiteSpace: 'pre-wrap' and
 *              wordBreak: 'break-word' so newlines + long URLs lay
 *              out cleanly.
 *   className — optional className passthrough
 */
export default function InlineMarkdown({ text, tag = 'div', style = {}, className }) {
  const Tag = tag;
  const html = renderInlineMarkdown(text || '');
  return (
    <Tag
      className={className}
      style={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        ...style,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
